'use client'

import * as React from 'react'
import { useUiEditorState } from '@/tiptap/hooks/use-ui-editor-state'
// Utils
import {
  getNodeDisplayName,
} from '@/tiptap/lib/tiptap-collab-utils'
// Icons
import { GripVerticalIcon } from '@/tiptap/tiptap-icons/grip-vertical-icon'
import { PaintBucketIcon } from '@/tiptap/tiptap-icons/paint-bucket-icon'
import { Repeat2Icon } from '@/tiptap/tiptap-icons/repeat-2-icon'
import { TextColorSmallIcon } from '@/tiptap/tiptap-icons/text-color-small-icon'
// Primitive UI Components
import { Button, ButtonGroup } from '@/tiptap/tiptap-ui-primitive/button'
import { Separator } from '@/tiptap/tiptap-ui-primitive/separator'
// Radix UI Components
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useBlockquote } from '@/tiptap/tiptap-ui/blockquote-button'
import { useCodeBlock } from '@/tiptap/tiptap-ui/code-block-button'
import {
  HIGHLIGHT_COLORS,
  useColorHighlight,
} from '@/tiptap/tiptap-ui/color-highlight-button'
import { TEXT_COLORS, useColorText } from '@/tiptap/tiptap-ui/color-text-button'
import type { RecentColor } from '@/tiptap/tiptap-ui/color-text-popover'
import {
  getColorByValue,
  useRecentColors,
} from '@/tiptap/tiptap-ui/color-text-popover'
import {
  CopyToClipboardShortcutBadge,
  useCopyToClipboard,
} from '@/tiptap/tiptap-ui/copy-to-clipboard-button'
import {
  DeleteNodeShortcutBadge,
  useDeleteNode,
} from '@/tiptap/tiptap-ui/delete-node-button'
import {
  DuplicateShortcutBadge,
  useDuplicate,
} from '@/tiptap/tiptap-ui/duplicate-button'
import { useHeading } from '@/tiptap/tiptap-ui/heading-button'
// Tiptap UI
import { useImageDownload } from '@/tiptap/tiptap-ui/image-download-button'
import { useList } from '@/tiptap/tiptap-ui/list-button'
import { useResetAllFormatting } from '@/tiptap/tiptap-ui/reset-all-formatting-button'
import { SlashCommandTriggerButton } from '@/tiptap/tiptap-ui/slash-command-trigger-button'
// import {
//   AskAiShortcutBadge,
//   useAiAsk,
// } from "@/tiptap/tiptap-ui/ai-ask-button"
import { useText } from '@/tiptap/tiptap-ui/text-button'
import { DragHandle } from '@tiptap/extension-drag-handle-react'
import type { Node as TiptapNode } from '@tiptap/pm/model'
import { useIsMobile } from '@/hooks/use-mobile'
// Hooks
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'
import { useMenuActionVisibility } from './drag-context-menu-hooks'
import type {
  ColorMenuItemProps,
  DragContextMenuProps,
  MenuItemProps,
  NodeChangeData,
} from './drag-context-menu-types'

const useNodeTransformActions = () => {
  const text = useText()
  const heading1 = useHeading({ level: 1 })
  const heading2 = useHeading({ level: 2 })
  const heading3 = useHeading({ level: 3 })
  const bulletList = useList({ type: 'bulletList' })
  const orderedList = useList({ type: 'orderedList' })
  const taskList = useList({ type: 'taskList' })
  const blockquote = useBlockquote()
  const codeBlock = useCodeBlock()

  const mapper = (
    action: ReturnType<
      | typeof useText
      | typeof useHeading
      | typeof useList
      | typeof useBlockquote
      | typeof useCodeBlock
    >
  ) => ({
    icon: action.Icon,
    label: action.label,
    onClick: action.handleToggle,
    disabled: !action.canToggle,
    isActive: action.isActive,
  })

  return [
    mapper(text),
    ...[heading1, heading2, heading3].map(mapper),
    mapper(bulletList),
    mapper(orderedList),
    mapper(taskList),
    mapper(blockquote),
    mapper(codeBlock),
  ]
}

const BaseMenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  isActive = false,
  shortcutBadge,
}) => (
  <DropdownMenuItem
    onClick={onClick}
    disabled={disabled}
    className={isActive ? 'bg-accent' : ''}
  >
    <Icon className='mr-2 h-4 w-4' />
    <span>{label}</span>
    {shortcutBadge && <span className='ml-auto'>{shortcutBadge}</span>}
  </DropdownMenuItem>
)

const SubMenuTrigger: React.FC<{
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
}> = ({ icon: Icon, label, children }) => (
  <DropdownMenuSub>
    <DropdownMenuSubTrigger>
      <Icon className='mr-2 h-4 w-4' />
      <span>{label}</span>
    </DropdownMenuSubTrigger>
    <DropdownMenuPortal>
      <DropdownMenuSubContent>
        {children}
      </DropdownMenuSubContent>
    </DropdownMenuPortal>
  </DropdownMenuSub>
)

const TextColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorText, label } = useColorText({
    label: color.label,
    textColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: 'text', label, value: color }),
  })

  return (
    <DropdownMenuItem
      onClick={handleColorText}
      className={isActive ? 'bg-accent' : ''}
    >
      <span className='mr-2' style={{ color: color.value }}>
        <TextColorSmallIcon
          className='h-4 w-4'
          style={{ color: color.value }}
        />
      </span>
      <span>{label}</span>
    </DropdownMenuItem>
  )
}

const HighlightColorMenuItem: React.FC<ColorMenuItemProps> = ({ color }) => {
  const { addRecentColor } = useRecentColors()
  const { isActive, handleColorHighlight, label } = useColorHighlight({
    label: color.label,
    highlightColor: color.value,
    onApplied: ({ color, label }) =>
      addRecentColor({ type: 'highlight', label, value: color }),
  })

  return (
    <DropdownMenuItem
      onClick={handleColorHighlight}
      className={isActive ? 'bg-accent' : ''}
    >
      <span
        className='mr-2 h-4 w-4 rounded'
        style={{ backgroundColor: color.value }}
      />
      <span>{label}</span>
    </DropdownMenuItem>
  )
}

const RecentColorMenuItem: React.FC<{
  colorObj: RecentColor
}> = ({ colorObj }) => {
  const colorSet = colorObj.type === 'text' ? TEXT_COLORS : HIGHLIGHT_COLORS
  const color = getColorByValue(colorObj.value, colorSet)

  const ColorComponent =
    colorObj.type === 'text' ? TextColorMenuItem : HighlightColorMenuItem

  return <ColorComponent color={color} />
}

const ColorActionGroup: React.FC = () => {
  const { recentColors, isInitialized } = useRecentColors()

  return (
    <SubMenuTrigger icon={PaintBucketIcon} label='Color'>
      {/* Recent Colors */}
      {isInitialized && recentColors.length > 0 && (
        <>
          <DropdownMenuGroup>
            <DropdownMenuLabel>Recent colors</DropdownMenuLabel>
            {recentColors.map((colorObj) => (
              <RecentColorMenuItem key={colorObj.value} colorObj={colorObj} />
            ))}
          </DropdownMenuGroup>
          <Separator />
        </>
      )}

      {/* Text Colors */}
      <DropdownMenuGroup>
        <DropdownMenuLabel>Text color</DropdownMenuLabel>
        {TEXT_COLORS.map((textColor) => (
          <TextColorMenuItem key={textColor.value} color={textColor} />
        ))}
      </DropdownMenuGroup>

      <Separator />

      {/* Highlight Colors */}
      <DropdownMenuGroup>
        <DropdownMenuLabel>Highlight color</DropdownMenuLabel>
        {HIGHLIGHT_COLORS.map((highlightColor) => (
          <HighlightColorMenuItem
            key={highlightColor.value}
            color={highlightColor}
          />
        ))}
      </DropdownMenuGroup>
    </SubMenuTrigger>
  )
}

const TransformActionGroup: React.FC = () => {
  const actions = useNodeTransformActions()
  return (
    <SubMenuTrigger icon={Repeat2Icon} label='Turn Into'>
      <DropdownMenuGroup>
        <DropdownMenuLabel>Turn into</DropdownMenuLabel>
        {actions.map((action) => (
          <BaseMenuItem key={action.label} {...action} />
        ))}
      </DropdownMenuGroup>
    </SubMenuTrigger>
  )
}

const ResetFormattingAction: React.FC = () => {
  const { canReset, handleResetFormatting, label, Icon } =
    useResetAllFormatting({
      hideWhenUnavailable: true,
      preserveMarks: ['inlineThread'],
    })

  return (
    <BaseMenuItem
      icon={Icon}
      label={label}
      disabled={!canReset}
      onClick={handleResetFormatting}
    />
  )
}

const ImageActionGroup: React.FC = () => {
  const { canDownload, handleDownload, label, Icon } = useImageDownload({
    hideWhenUnavailable: true,
  })

  return (
    <>
      <BaseMenuItem
        icon={Icon}
        label={label}
        disabled={!canDownload}
        onClick={handleDownload}
      />

      <Separator orientation='horizontal' />
    </>
  )
}

const CoreActionGroup: React.FC = () => {
  const {
    handleDuplicate,
    canDuplicate,
    label,
    Icon: DuplicateIcon,
  } = useDuplicate()
  const {
    handleCopyToClipboard,
    canCopyToClipboard,
    label: copyLabel,
    Icon: CopyIcon,
  } = useCopyToClipboard()

  return (
    <>
      <DropdownMenuGroup>
        <BaseMenuItem
          icon={DuplicateIcon}
          label={label}
          onClick={handleDuplicate}
          disabled={!canDuplicate}
          shortcutBadge={<DuplicateShortcutBadge />}
        />
        <BaseMenuItem
          icon={CopyIcon}
          label={copyLabel}
          onClick={handleCopyToClipboard}
          disabled={!canCopyToClipboard}
          shortcutBadge={<CopyToClipboardShortcutBadge />}
        />
      </DropdownMenuGroup>

      <Separator />
    </>
  )
}

// const AIActionGroup: React.FC = () => {
//   const { handleAiAsk, canAiAsk, Icon: AiAskIcon } = useAiAsk()

//   if (!canAiAsk) return null

//   return (
//     <>
//       <MenuGroup>
//         {canAiAsk && (
//           <BaseMenuItem
//             icon={AiAskIcon}
//             label="Ask AI"
//             onClick={handleAiAsk}
//             shortcutBadge={<AskAiShortcutBadge />}
//           />
//         )}
//       </MenuGroup>

//       <Separator orientation="horizontal" />
//     </>
//   )
// }

const DeleteActionGroup: React.FC = () => {
  const { handleDeleteNode, canDeleteNode, label, Icon } = useDeleteNode()

  return (
    <DropdownMenuGroup>
      <BaseMenuItem
        icon={Icon}
        label={label}
        onClick={handleDeleteNode}
        disabled={!canDeleteNode}
        shortcutBadge={<DeleteNodeShortcutBadge />}
      />
    </DropdownMenuGroup>
  )
}

export const DragContextMenu: React.FC<DragContextMenuProps> = ({
  editor: providedEditor,
  withSlashCommandTrigger = true,
  ...props
}) => {
  const { editor } = useTiptapEditor(providedEditor)

  const { aiGenerationActive } = useUiEditorState(editor)
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(false)
  const [node, setNode] = React.useState<TiptapNode | null>(null)
  const [nodePos, setNodePos] = React.useState<number>(-1)

  const handleNodeChange = React.useCallback((data: NodeChangeData) => {
    if (data.node) setNode(data.node)
    setNodePos(data.pos)
  }, [])

  // Set node selection when menu opens, so duplicate/delete actions work on the correct node
  React.useEffect(() => {
    if (!editor) return
    editor.commands.setLockDragHandle(open)
    editor.commands.setMeta('lockDragHandle', open)

    if (open && nodePos >= 0) {
      editor.commands.setNodeSelection(nodePos)
    }
  }, [editor, open, nodePos])

  const {
    hasAnyActionGroups,
    // hasColorActions,
    hasTransformActions,
    hasResetFormatting,
    hasImage,
  } = useMenuActionVisibility(editor)

  if (!editor) return null

  const isEditable = editor.isEditable

  return (
    <div style={!isEditable ? { display: 'none' } : undefined}>
      <DragHandle
        editor={editor}
        onNodeChange={handleNodeChange}
        {...props}
        className='relative -right-4.5 pr-2.5 pt-0.5 pb-0 -z-10'
      >
        <ButtonGroup
          orientation='horizontal'
          style={{
            ...(aiGenerationActive ||
            isMobile ||
            !isEditable
              ? { opacity: 0, pointerEvents: 'none' }
              : {}),
          }}
        >
          {withSlashCommandTrigger && (
            <SlashCommandTriggerButton
              node={node}
              nodePos={nodePos}
              data-weight='small'
              style={{
                paddingLeft: '0px',
                paddingRight: '0px',
                width: '28px',
                height: '28px',
                minWidth: '28px',
              }}
            />
          )}

          <DropdownMenu
            open={open}
            onOpenChange={setOpen}
            modal={false}
          >
            <DropdownMenuTrigger asChild>
              <Button
                data-style='ghost'
                tabIndex={-1}
                tooltip={
                  <>
                    <div>Click for options</div>
                    <div>Hold for drag</div>
                  </>
                }
                data-weight='small'
                style={{
                  cursor: 'grab',
                  width: '28px',
                  height: '28px',
                  paddingLeft: '0px',
                  paddingRight: '0px',
                  minWidth: '28px',
                }}
                onMouseDown={(e) => {
                  if (!editor) return
                  // Prevent drag on mousedown when menu is open
                  if (open) {
                    e.preventDefault()
                    e.stopPropagation()
                  } else {
                    editor.commands.setNodeSelection(nodePos)
                  }
                }}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <GripVerticalIcon className='tiptap-button-icon' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-64'
              side='left'
              align='start'
              sideOffset={0}
              alignOffset={0}
              onCloseAutoFocus={(e) => e.preventDefault()}
              forceMount={open ? true : undefined}
            >
              <DropdownMenuGroup>
                <DropdownMenuLabel>{getNodeDisplayName(editor)}</DropdownMenuLabel>
                {/* {hasColorActions && <ColorActionGroup />} */}
                {hasTransformActions && <TransformActionGroup />}
                {hasResetFormatting && <ResetFormattingAction />}
                {hasImage && <ImageActionGroup />}
              </DropdownMenuGroup>

              {hasAnyActionGroups && <Separator />}

              <CoreActionGroup />

              {/* <AIActionGroup /> */}

              <DeleteActionGroup />
            </DropdownMenuContent>
          </DropdownMenu>
        </ButtonGroup>
      </DragHandle>
    </div>
  )
}
